import { CCol } from "@coreui/react"
import { ReactComponent as NotAccessible } from "assets/icons/sideBar/not_accessible.svg"
import { useTheme } from "components/common/Context/ThemeContext"
import { NOT_ACCESSIBLE_TO_CLIENT } from "components/common/noData/constants"
import PropTypes from "prop-types"
import React from "react"
import { useSelector } from "react-redux"

import CMSCard from "../cards/CMSCards"
import { Tooltip } from "../tooltip"
import CMSTypography, {
  fontSizes,
  StandardTypographyTypes
} from "../typography/CMSTypography"

/**
 * Component to render each each element inside the SLA Card
 *
 * @example
 * const metric = {
 *   onClick?: func,
 *   text: "SLA Missed",
 *   data: totalSlaResolutionBreach,
 *   icon?: MissedIcon,
 *   className?: "itsm-missedIcon",
 * }
 *
 * return (
 *  <SLACardElement
 *    totalMetrics={5}
 *    metric={metric}
 *    index={index}
 *  />
 * )
 */
function SLACardElement({ totalMetrics, metric, index }) {
  const Icon = metric.icon
  return (
    <CCol
      className={`px-0 ${
        index === totalMetrics - 1
          ? ""
          : `dotted-border-right ${
              index === 0 ? "d-flex flex-column justify-content-center" : ""
            }`
      } ${metric.onClick ? "cursor-pointer" : ""}`}
      key={index}
      onClick={() => metric.onClick && metric.onClick()}
    >
      <div className="d-flex align-items-center justify-content-center">
        {index !== totalMetrics - 1 ? (
          <>
            {metric.icon && (
              <span className={`${metric.className} itsm-slaIcons`}>
                <Icon />
              </span>
            )}
            <div data-testid={`${metric.text} ${index}`}>{metric.data}</div>
          </>
        ) : (
          <>
            <div data-testid={`${metric.text} ${index}`}>
              <CMSTypography
                type={StandardTypographyTypes.subHeading}
              >{`${metric.data}%`}</CMSTypography>
            </div>
          </>
        )}
      </div>
      <div className="align-center">
        <CMSTypography type={StandardTypographyTypes.cardContent}>
          {metric.text}
        </CMSTypography>
      </div>
    </CCol>
  )
}

SLACardElement.propTypes = {
  // total number of elements in the card
  totalMetrics: PropTypes.number,
  // single metric of the element to render the data
  metric: PropTypes.object,
  // index of the element in the card
  index: PropTypes.number
}

/**
 * Component to render SLA Card for SLA Missed and SLA acchieved on overall analytics page
 * @example
 * const metrics = [
 *  {
 *    onClick?: func,
 *    text: "SLA Missed",
 *    data: 4,
 *    icon?: MissedIcon,
 *    className?: "itsm-missedIcon",
 *  },
 *  {
 *    onClick?: func,
 *    text: "SLA Achieved",
 *    data: 4,
 *    icon?: MissedIcon,
 *    className?: "itsm-missedIcon",
 *  }
 * ]
 * return (
 *  <SLACard
 *    title= "Test Title",
 *    helpText= "Test Help Text",
 *    helpTextHeading= "Test Help Text Heading",
 *    metrics={metrics}
 *  />
 * )
 */
function SLACard({ title, metrics, helpText, helpTextHeading }) {
  const allowedPortalComponents = useSelector(
    (state) => state.user.allowedPortalComponents
  )
  const theme = useTheme()
  return (
    <CMSCard>
      <div className="d-flex align-items-center">
        <CMSTypography type={StandardTypographyTypes.cardHeading}>
          {title}
        </CMSTypography>
        <Tooltip text={helpText} heading={helpTextHeading} />
        {allowedPortalComponents.itsm_sla === NOT_ACCESSIBLE_TO_CLIENT && (
          <NotAccessible className="not-accessible-icon" />
        )}
      </div>
      {allowedPortalComponents.itsm_sla === NOT_ACCESSIBLE_TO_CLIENT && (
        <CMSTypography fontSize={fontSizes.smaller} color={theme.nonComplColor}>
          {NOT_ACCESSIBLE_TO_CLIENT}
        </CMSTypography>
      )}

      <div className="d-flex p-0">
        {metrics.map((metric, index) => (
          <SLACardElement
            totalMetrics={metrics.length}
            metric={metric}
            index={index}
            key={index}
          />
        ))}
      </div>
    </CMSCard>
  )
}

SLACard.propTypes = {
  // The card title
  title: PropTypes.string,
  // The tooltip heading
  helpTextHeading: PropTypes.string,
  // The tooltip content
  helpText: PropTypes.node,
  // metrics to render data inside the card
  metrics: PropTypes.array
}

export default SLACard
